/**
 * 뷰 스펙(UI 프리미티브 선언) — 결정 #27. 게임 모듈이 "무엇을 렌더할지"를
 * 데이터로 선언하면, web 렌더러가 대응 위젯을 그린다. 임의 React 코드 없이도
 * 다양한 게임 UI를 구성하게 해 데이터형 게임(#29)을 가능하게 한다.
 *
 * 플랫폼 중립(결정 #10): 여기는 "어떤 위젯을 어떤 데이터로"를 기술하는 타입만
 * 정의한다. 실제 렌더링(React 컴포넌트)은 web의 프리미티브 레이어가 담당한다.
 * 보안(결정 #17): 위젯은 화이트리스트 — 알 수 없는 종류는 렌더러가 무시한다.
 */

/** 선택지 목록(텍스트 어드벤처·VN 분기). 사용자가 고르면 그 텍스트가 입력이 된다. */
export interface ChoicesWidget {
  kind: "choices";
  choices: string[];
  /** 비활성(예: 생성 중). */
  disabled?: boolean;
}

/** 단일 스탯 막대(HP/MP/호감도 등). value/max로 비율 표시. */
export interface StatBarWidget {
  kind: "stat-bar";
  label: string;
  value: number;
  max: number;
  /** 색상 힌트(렌더러가 해석). 예: "hp"=빨강, "mp"=파랑. */
  tone?: string;
}

/** 여러 스탯을 한 패널로(라벨:값). 막대가 필요 없는 수치 묶음. */
export interface StatPanelWidget {
  kind: "stat-panel";
  title?: string;
  stats: { label: string; value: string | number }[];
}

/** 인벤토리/아이템 그리드. 각 항목은 이름 + 선택적 아이콘/수량/에셋. */
export interface InventoryWidget {
  kind: "inventory";
  title?: string;
  items: { name: string; qty?: number; icon?: string; assetId?: string }[];
}

/** 스프라이트 무대(비주얼노벨). 배경 + 캐릭터 스프라이트 레이어. */
export interface SpriteStageWidget {
  kind: "sprite-stage";
  /** 배경 에셋 id 또는 키. */
  backgroundAssetId?: string;
  /** 무대 위 스프라이트(에셋 id + 위치). */
  sprites: {
    assetId?: string;
    /** 감정/표현 키(emotionAssetIds 조회용). */
    emotion?: string;
    characterId?: string;
    position?: "left" | "center" | "right";
  }[];
}

/** 주사위 결과 표시(스탯 RPG). 굴림 표기 + 결과 값. */
export interface DiceWidget {
  kind: "dice";
  notation: string;
  result: number;
  /** 판정 성공 여부(있으면 강조). */
  success?: boolean;
}

/** 투표(소셜 추리/마피아). 후보 목록 + 진행 상태. */
export interface VoteWidget {
  kind: "vote";
  title?: string;
  candidates: { id: string; label: string; votes?: number }[];
  /** 이미 투표했는지(중복 방지 UI). */
  voted?: boolean;
}

/** 자유 형식 안내/알림 배너(상태 변화 요약 등). */
export interface NoticeWidget {
  kind: "notice";
  text: string;
  tone?: "info" | "success" | "warning" | "danger";
}

/** 위젯 판별 유니온(결정 #27 화이트리스트). 새 위젯은 여기에 추가. */
export type ViewWidget =
  | ChoicesWidget
  | StatBarWidget
  | StatPanelWidget
  | InventoryWidget
  | SpriteStageWidget
  | DiceWidget
  | VoteWidget
  | NoticeWidget;

/** 위젯 종류 키 집합(런타임 검증용). */
export const VIEW_WIDGET_KINDS = [
  "choices",
  "stat-bar",
  "stat-panel",
  "inventory",
  "sprite-stage",
  "dice",
  "vote",
  "notice",
] as const;

/**
 * 한 화면의 뷰 스펙(결정 #27). 위젯들을 순서대로 배치한다. 모듈의 viewSpec(state)이
 * 이걸 반환하고, web 렌더러가 region/widgets를 그린다.
 */
export interface ViewSpec {
  /** 입력창 위(메시지 영역 하단)에 붙일 위젯들. */
  widgets: ViewWidget[];
  /** 헤더/사이드에 고정 표시할 위젯들(스탯 패널 등). 옵션. */
  hud?: ViewWidget[];
}

/** 알 수 없는 종류의 위젯을 걸러낸다(결정 #17 화이트리스트 안전장치). */
export function sanitizeViewSpec(spec: ViewSpec | undefined): ViewSpec {
  if (!spec) return { widgets: [] };
  const allowed = new Set<string>(VIEW_WIDGET_KINDS);
  const filter = (ws: ViewWidget[] | undefined): ViewWidget[] =>
    (ws ?? []).filter((w) => allowed.has(w.kind));
  const out: ViewSpec = { widgets: filter(spec.widgets) };
  const hud = filter(spec.hud);
  if (hud.length) out.hud = hud;
  return out;
}
